import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import type { GenerateProductionPickListRequest, ProductionOrderDto, ProductionPickListDto, ProductionPickListLineDto } from '@/types/production';

export default function ProductionPickListPage() {
  const { t } = useTranslation('production');
  const queryClient = useQueryClient();

  const { data: pickLists = [], isLoading } = useQuery({
    queryKey: ['production-pick-lists'],
    queryFn: async () => (await apiClient.get<ProductionPickListDto[]>('/production-pick-lists')).data,
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: async () => (await apiClient.get<ProductionOrderDto[]>('/production-orders')).data,
  });

  // Only a released, still-open order with no pick list yet can have one generated.
  const pickListedOrderIds = new Set(pickLists.map((p) => p.productionOrderId));
  const generatableOrders = orders.filter((o) => o.status === 'Released' && !o.isClosed && !pickListedOrderIds.has(o.id));

  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [viewing, setViewing] = useState<ProductionPickListDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['production-pick-lists'] });

  const generateMutation = useMutation({
    mutationFn: () => {
      const payload: GenerateProductionPickListRequest = { productionOrderId: orderId };
      return apiClient.post('/production-pick-lists/generate', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
      setOrderId('');
    },
  });

  const columns: DataTableColumn<ProductionPickListDto>[] = [
    { field: 'pickListNumber', headerName: t('pickList.table.number') },
    { field: 'productionOrderNumber', headerName: t('table.productionOrder'), filterable: true },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Button size="small" onClick={() => setViewing(r)}>{t('view')}</Button>
      ),
    },
  ];

  const lineColumns: DataTableColumn<ProductionPickListLineDto>[] = [
    { field: 'materialCode', headerName: t('table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'barcode', headerName: t('pickList.barcode'), render: (l) => l.barcode ?? '—' },
    { field: 'locationCode', headerName: t('pickList.location'), render: (l) => l.locationCode ?? '—' },
    { field: 'batchNo', headerName: t('pickList.batch'), render: (l) => l.batchNo ?? '—' },
    { field: 'requiredQty', headerName: t('pickList.individualQty') },
    { field: 'packingQty', headerName: t('pickList.packingQty'), render: (l) => l.packingQty ?? '—' },
    { field: 'pickedQty', headerName: t('table.pickedQty') },
    { field: 'status', headerName: t('table.status'), render: (l) => <StatusChip status={l.status} /> },
  ];

  return (
    <>
      <PageHeader
        title={t('pickList.title')}
        subtitle={t('pickList.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.PRODUCTION_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              {t('pickList.generate')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={pickLists} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="production-pick-lists" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('pickList.generate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('pickList.selectOrder')} value={orderId} onChange={setOrderId} fullWidth helperText={t('pickList.selectOrderHint')}
              options={generatableOrders.map((o) => ({ value: o.id, label: `${o.orderNumber} — ${o.materialCode}` }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={!orderId || generateMutation.isPending} onClick={() => generateMutation.mutate()}>
            {t('pickList.generate')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.pickListNumber ?? ''}
        subtitle={viewing ? `${viewing.productionOrderNumber} · ${viewing.warehouseName}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
